import React from 'react';
import { motion } from 'framer-motion';
import { Handshake, GraduationCap, Building2, Users, Globe, Lightbulb, FileText, ArrowRight } from 'lucide-react';
export function PartnershipsPage() {
  const partnershipTypes = [{
    icon: <GraduationCap size={28} data-id="element-1433" data-mp="2ux9384tyhmvp" />,
    title: 'Educational Institutions',
    color: 'bg-teal',
    description: 'Partner with universities, schools, and learning systems to integrate judgment development, reasoning quality, and civic capability into curricula and student development programs.'
  }, {
    icon: <Building2 size={28} data-id="element-1434" data-mp="2b6xc2l9a8dfc" />,
    title: 'Employers & Organizations',
    color: 'bg-navy-light',
    description: 'Collaborate with companies and workforce programs to strengthen decision quality, leadership pipelines, and advancement readiness across teams and talent development initiatives.'
  }, {
    icon: <Users size={28} data-id="element-1435" data-mp="3ijpbr8m1ws5v" />,
    title: 'Civil Society Organizations',
    color: 'bg-green-soft',
    description: 'Work with nonprofits, community groups, and mentorship networks to build misinformation resilience, opportunity pathways, and local leadership capacity.'
  }, {
    icon: <Globe size={28} data-id="element-1436" data-mp="2qlfk84lr1mhm" />,
    title: 'Government & Public Institutions',
    color: 'bg-navy',
    description: 'Support municipal, regional, and national governments in workforce modernization, civic resilience planning, civil service development, and leadership qualification frameworks.'
  }, {
    icon: <Lightbulb size={28} data-id="element-1437" data-mp="22v1nw1w4t1h8" />,
    title: 'Research Institutions',
    color: 'bg-teal',
    description: 'Engage with academic researchers, think tanks, and policy institutes to advance the science of judgment, decision-quality, manipulation resistance, and institutional readiness.'
  }, {
    icon: <FileText size={28} data-id="element-1438" data-mp="1f34lt2ihhntb" />,
    title: 'Technology & Platform Partners',
    color: 'bg-navy-light',
    description: 'Integrate with complementary platforms, assessment tools, learning management systems, and HR technologies to expand reach and create seamless user experiences.'
  }];
  return <div className="min-h-screen bg-surface-bg" data-id="element-1439" data-mp="2w7i3wrvz42l4">
      {/* Hero Section */}
      <section className="pt-32 pb-20 lg:pt-40 lg:pb-28 px-4 sm:px-6 lg:px-8 max-w-5xl mx-auto" data-id="element-1440" data-mp="2w9m695e84tja">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} transition={{
        duration: 0.6
      }} className="text-center" data-id="element-1441" data-mp="2s80yfygjr5fy">
          <div className="inline-flex items-center gap-2 mb-6" data-id="element-1442" data-mp="2dilaeq8ehe3y">
            <span className="w-8 h-px bg-teal" data-id="element-1443" data-mp="28hntsi31gs4k"></span>
            <span className="text-xs font-semibold text-teal uppercase tracking-[0.2em]" data-id="element-1444" data-mp="28k1kyfullk6u">
              Partnerships
            </span>
            <span className="w-8 h-px bg-teal" data-id="element-1445" data-mp="3jxlzcbz0cw11"></span>
          </div>

          <h1 className="text-4xl sm:text-5xl lg:text-6xl font-heading font-bold text-ink-dark leading-tight mb-8" data-id="element-1446" data-mp="3jzghjiiwaa1f">
            Building the Future of Human Capability Together
          </h1>

          <p className="text-xl text-ink-muted leading-relaxed max-w-3xl mx-auto" data-id="element-1447" data-mp="7z7dohutm34">
            SyNeural is designed for collaboration. We partner with educational
            institutions, employers, civil society organizations, governments,
            research institutions, and technology platforms to strengthen
            judgment, expand opportunity, and build more resilient systems.
          </p>
        </motion.div>
      </section>

      {/* Introduction */}
      <section className="py-16 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1448" data-mp="58vmky7nounu">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1449" data-mp="a7q1vq6f0137">
          <p className="text-lg text-ink-muted leading-relaxed mb-6" data-id="element-1450" data-mp="3a9f1yn1bng4g">
            Our platform is most effective when integrated into broader
            ecosystems of development, opportunity, and institutional support.
            We work with partners who share our commitment to strengthening
            human capability, improving decision quality, and creating pathways
            from potential to responsibility.
          </p>
          <p className="text-lg text-ink-muted leading-relaxed" data-id="element-1451" data-mp="3tyfqia4adnva">
            Whether you're an educational institution looking to strengthen
            reasoning and civic development, an employer focused on leadership
            pipelines, a government modernizing civil service, or a research
            organization advancing the science of judgment — we're interested in
            exploring how we can work together.
          </p>
        </motion.div>
      </section>

      {/* Partnership Types */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 max-w-6xl mx-auto" data-id="element-1452" data-mp="box2ix5vfnyb">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} className="mb-16 text-center" data-id="element-1453" data-mp="j3fwjxqgvnzl">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-4" data-id="element-1454" data-mp="121ab0rad6rpd">
            Partnership Opportunities
          </h2>
          <p className="text-lg text-ink-muted max-w-2xl mx-auto" data-id="element-1455" data-mp="16yiucv7911s7">
            We're open to partnerships across multiple sectors and scales
          </p>
        </motion.div>

        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-8" data-id="element-1456" data-mp="1gpwoengfl9dy">
          {partnershipTypes.map((type, index) => <motion.div key={type.title} initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: index * 0.1
        }} className="bg-white p-8 rounded-xl border border-line shadow-sm hover:shadow-md transition-shadow" data-id="element-1457" data-mp="1lqazyfh8r1gq" data-mpi={index}>
              <div className={`w-14 h-14 rounded-lg ${type.color} flex items-center justify-center text-white mb-6`} data-id="element-1458" data-mp="9bjk2u11m93x">
                {type.icon}
              </div>
              <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1459" data-mp="ichh2sd2dgd0">
                {type.title}
              </h3>
              <p className="text-ink-muted leading-relaxed" data-id="element-1460" data-mp="ec7611aezzra">
                {type.description}
              </p>
            </motion.div>)}
        </div>
      </section>

      {/* What We Offer Partners */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 bg-surface-muted border-y border-line" data-id="element-1461" data-mp="2g43l2kvsjrqt">
        <div className="max-w-5xl mx-auto" data-id="element-1462" data-mp="25z3xt4qaf4n8">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1463" data-mp="2a05x1lk7fgp7">
            <h2 className="text-3xl font-heading font-bold text-ink-dark mb-12 text-center" data-id="element-1464" data-mp="1m9tihrugelpn">
              What We Offer Partners
            </h2>

            <div className="grid grid-cols-1 md:grid-cols-2 gap-8" data-id="element-1465" data-mp="1r7bl70hvo4p7">
              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1466" data-mp="1dbhmfaativpt">
                <div className="w-12 h-12 rounded-lg bg-navy/10 flex items-center justify-center text-navy mb-4" data-id="element-1467" data-mp="1ia15n2y11teh">
                  <Handshake size={24} data-id="element-1468" data-mp="37oh5jdeeq81u" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1469" data-mp="3cnk9euini888">
                  Platform Access & Integration
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1470" data-mp="p1ajfenhq48a">
                  Access to CJI and NCJI frameworks, customized deployment
                  models, API integration support, and technical collaboration.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1471" data-mp="bbgqol8v6zta">
                <div className="w-12 h-12 rounded-lg bg-teal/10 flex items-center justify-center text-teal mb-4" data-id="element-1472" data-mp="6bax2zj1b38k">
                  <Lightbulb size={24} data-id="element-1473" data-mp="1c81e5oy29d9" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1474" data-mp="1eu8k47xamuy">
                  Research Collaboration
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1475" data-mp="3ohyvy7nhqu5s">
                  Joint research initiatives, pilot program support, data
                  insights, and co-development of frameworks and methodologies.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1476" data-mp="1mnzra54vjb22">
                <div className="w-12 h-12 rounded-lg bg-green-soft/10 flex items-center justify-center text-green-soft mb-4" data-id="element-1477" data-mp="34n75x9zqi7mn">
                  <Users size={24} data-id="element-1478" data-mp="3ed8sfxcfvhzg" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1479" data-mp="3edscpkpkaign">
                  Implementation Support
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1480" data-mp="2lol57w84qij9">
                  Training, onboarding assistance, best practices guidance, and
                  ongoing partnership management.
                </p>
              </div>

              <div className="bg-white p-8 rounded-lg border border-line" data-id="element-1481" data-mp="2uo85uft1bkb9">
                <div className="w-12 h-12 rounded-lg bg-navy-light/10 flex items-center justify-center text-navy-light mb-4" data-id="element-1482" data-mp="2zo4747ackxn4">
                  <Globe size={24} data-id="element-1483" data-mp="9y0owpizmq2q" />
                </div>
                <h3 className="text-xl font-heading font-bold text-ink-dark mb-3" data-id="element-1484" data-mp="ey5328wfb8ps">
                  Strategic Alignment
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1485" data-mp="h7akc8cuxrps">
                  Co-marketing opportunities, thought leadership collaboration,
                  and alignment on long-term vision and impact goals.
                </p>
              </div>
            </div>
          </motion.div>
        </div>
      </section>

      {/* Partnership Principles */}
      <section className="py-20 px-4 sm:px-6 lg:px-8 max-w-4xl mx-auto" data-id="element-1486" data-mp="3ueu98nmkfgvm">
        <motion.div initial={{
        opacity: 0,
        y: 20
      }} whileInView={{
        opacity: 1,
        y: 0
      }} viewport={{
        once: true
      }} transition={{
        duration: 0.6
      }} data-id="element-1487" data-mp="3ugylbsumbm6z">
          <h2 className="text-3xl font-heading font-bold text-ink-dark mb-8 text-center" data-id="element-1488" data-mp="c6wemwyqatux">
            Our Partnership Principles
          </h2>

          <div className="space-y-6" data-id="element-1489" data-mp="1nnmmekisvtqn">
            <div className="flex gap-4" data-id="element-1490" data-mp="1nml4bw3da6zf">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1491" data-mp="386yk4rkisoet"></div>
              <div data-id="element-1492" data-mp="344tv6650ofyi">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1493" data-mp="344lee35rwqi1">
                  Shared Mission
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1494" data-mp="2zck6etfn1lxt">
                  We partner with organizations committed to strengthening human
                  capability, improving judgment quality, and creating
                  meaningful pathways for development and opportunity.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-1495" data-mp="2gf99hfhhb4lj">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1496" data-mp="2ggaileexph0m"></div>
              <div data-id="element-1497" data-mp="2bgxyh1fg6o1q">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1498" data-mp="2bhhf8etd36e6">
                  Long-Term Orientation
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1499" data-mp="eljq4n8yp4h6">
                  We're focused on partnerships that create lasting value, not
                  short-term transactions. We invest in relationships that can
                  grow and deepen over time.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-1500" data-mp="jszyd19y1arr">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1501" data-mp="ju1c5dicgbd6"></div>
              <div data-id="element-1502" data-mp="osw0euz2ys4o">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1503" data-mp="oscgj8pvdto0">
                  Mutual Learning
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1504" data-mp="1cfkl1ofh4yoz">
                  The best partnerships are bidirectional. We learn from our
                  partners' expertise, context, and feedback to continuously
                  improve our platform and approach.
                </p>
              </div>
            </div>

            <div className="flex gap-4" data-id="element-1505" data-mp="3n8x8xbc0okqm">
              <div className="w-2 h-2 rounded-full bg-teal mt-2 flex-shrink-0" data-id="element-1506" data-mp="3n7vowhd0wnwr"></div>
              <div data-id="element-1507" data-mp="3s9cjxw8ttg5f">
                <h3 className="font-heading font-bold text-ink-dark mb-2" data-id="element-1508" data-mp="3s9urhp0xvgsn">
                  Real-World Impact
                </h3>
                <p className="text-ink-muted leading-relaxed" data-id="element-1509" data-mp="3fq7j138t7ihp">
                  We prioritize partnerships that translate into measurable
                  improvements in reasoning quality, opportunity access,
                  institutional readiness, and leadership development.
                </p>
              </div>
            </div>
          </div>
        </motion.div>
      </section>

      {/* CTA Section */}
      <section className="py-24 px-4 sm:px-6 lg:px-8 bg-navy-deep text-white" data-id="element-1510" data-mp="2wzzimswhv657">
        <div className="max-w-4xl mx-auto text-center" data-id="element-1511" data-mp="3aqunzv7sb541">
          <motion.div initial={{
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.6
        }} data-id="element-1512" data-mp="3asff1auw45x5">
            <h2 className="text-3xl font-heading font-bold mb-6" data-id="element-1513" data-mp="2qyfr5ery9vjl">
              Interested in Partnering with SyNeural?
            </h2>
            <p className="text-xl leading-relaxed opacity-90 mb-10" data-id="element-1514" data-mp="2vybst7d6davz">
              We're always open to conversations with organizations that share
              our vision for strengthening human capability and institutional
              readiness.
            </p>
            <a href="/contact" className="inline-flex items-center gap-2 bg-white text-navy-deep font-medium px-8 py-4 rounded-lg hover:bg-gray-100 transition-colors shadow-lg" data-id="element-1515" data-mp="39oxet4xxe0y3">
              Get in Touch
              <ArrowRight size={20} data-id="element-1516" data-mp="3jne9abw2oxow" />
            </a>
          </motion.div>
        </div>
      </section>
    </div>;
}